import dayjs from 'dayjs'
import { holidayDay } from '@vacationtracker/shared/functions/holiday-tags'

import { HolidayPatch, HolidayRow, HolidayUpdatePayload, rowKey } from './logic'
import type { RecurrenceSettings } from './recurrence'

/** What one cell asked for, kept as intent so its body can be rebuilt on whatever the row is by then. */
export type CellEdit = { field: 'name' | 'dates'; patch: HolidayPatch } | { field: 'recurring'; settings: RecurrenceSettings | null }

/** The row fields one edit sets, and only those, so dropping one edit never drops another's. */
export type EditFields = Partial<Pick<HolidayRow, 'name' | 'date' | 'endDate' | 'isHalfDay' | 'recurring' | 'recurringRules' | 'transferIfFallsOnNonWorkingDay'>>

export interface PendingEdit {
  seq: number
  /** The row keys the edited row goes by: where it was edited, plus where a saved move put it. */
  keys: string[]
  fields: EditFields
  /** Set once the backend reported the event done. */
  savedAt?: number
}

/** How long an optimistic value outranks the read model before the read model is taken as the truth. */
export const PENDING_EDIT_TTL_MS = 10_000

const endOf = (payload: HolidayUpdatePayload, start: string): string | undefined => {
  const duration = payload.recurringRules?.specificDayDuration
  const end = duration
    ? dayjs(start)
        .add(duration - 1, 'day')
        .format('YYYY-MM-DD')
    : holidayDay(payload.endDate)
  return end === start ? undefined : end
}

/** The fields an update body sets on the row, as the tab groups it: one row per span. */
export function editFields(edit: CellEdit, payload: HolidayUpdatePayload, before: HolidayRow): EditFields {
  if (edit.field === 'name') return { name: payload.name }
  const startDate = holidayDay(payload.startDate)
  const dates = { date: startDate, endDate: endOf(payload, startDate), isHalfDay: payload.isHalfDay }
  if (edit.field === 'dates') return dates
  const recurrence = { recurring: payload.recurring, recurringRules: payload.recurringRules, transferIfFallsOnNonWorkingDay: payload.transferIfFallsOnNonWorkingDay }
  const moved = dates.date !== holidayDay(before.date) || (dates.endDate ?? dates.date) !== holidayDay(before.endDate ?? before.date)
  return moved ? { ...recurrence, ...dates } : recurrence
}

export function editsOf(keys: string[], pending: PendingEdit[]): PendingEdit[] {
  return pending.filter((entry) => entry.keys.some((key) => keys.includes(key)))
}

export function applyEdits(row: HolidayRow, edits: PendingEdit[]): HolidayRow {
  return edits.reduce<HolidayRow>((current, edit) => ({ ...current, ...edit.fields }), row)
}

const sameDay = (a?: string, b?: string): boolean => (a ? holidayDay(a) : undefined) === (b ? holidayDay(b) : undefined)

// The fields the backend echoes back as sent. Rules and transfer are not compared: a moved
// transfer changes the stored date, which the row key already accounts for.
function caughtUp(row: HolidayRow, fields: EditFields): boolean {
  if ('name' in fields && row.name !== fields.name) return false
  if ('date' in fields && !sameDay(row.date, fields.date)) return false
  if ('date' in fields && !sameDay(row.endDate ?? row.date, fields.endDate ?? fields.date)) return false
  if ('isHalfDay' in fields && Boolean(row.isHalfDay) !== Boolean(fields.isHalfDay)) return false
  if ('recurring' in fields && Boolean(row.recurring) !== Boolean(fields.recurring)) return false
  return true
}

export interface PrunePendingInput {
  pending: PendingEdit[]
  /** Every row the read model now holds, across years. */
  rows: HolidayRow[]
  now: number
}

/**
 * The fields of an edit that no later saved edit of the same row sets again. An unsaved later edit
 * does not count: the queued bodies before it are still built on this edit's value.
 */
function liveFields(entry: PendingEdit, pending: PendingEdit[]): EditFields {
  const later = pending.filter((other) => other.seq > entry.seq && other.savedAt !== undefined && other.keys.some((key) => entry.keys.includes(key)))
  const overwritten = new Set(later.flatMap((other) => Object.keys(other.fields)))
  return Object.fromEntries(Object.entries(entry.fields).filter(([field]) => !overwritten.has(field))) as EditFields
}

// A saved edit also goes once its row is gone: a move landed somewhere it can no longer be told.
// The deadline exists because an accepted event can still fail, and the read model must win then.
export function prunePending({ pending, rows, now }: PrunePendingInput): PendingEdit[] {
  const byKey = new Map(rows.map((row) => [rowKey(row), row]))
  return pending.filter((entry) => {
    if (entry.savedAt === undefined) return true
    if (now - entry.savedAt > PENDING_EDIT_TTL_MS) return false
    const row = entry.keys.map((key) => byKey.get(key)).find(Boolean)
    // Renamed to "First" then "Second": once the row shows "Second", the earlier rename has nothing left to wait for.
    return Boolean(row) && !caughtUp(row as HolidayRow, liveFields(entry, pending))
  })
}
